"use client";

import { Button } from "@/components/ui/button";
import { Block, HiVis } from "@/types/editor/blocks";
import { characterShapes, Characters } from "@/config/font";
import { generateSVGPDF } from "@/server/actions/pdf/generate-pdf";
import { useState, useEffect } from "react";
import { Download } from "lucide-react";

interface DownloadCMYKPDFProps {
  debugMode: boolean;
  hiVis: HiVis | null;
  orderNumber?: string;
  partNumber?: string;
  decalNumber?: string;
}

// Move generateSVG outside the component to make it directly exportable
export const generateSVG = (
  block: Block,
  width: number,
  height: number,
  hiVis: HiVis,
  options?: {
    useNaturalWidth?: boolean;
  }
) => {
  // Default options
  const useNaturalWidth = options?.useNaturalWidth || false;

  // THRU_CUT_COLOR and utility functions
  const THRU_CUT_COLOR = "#6569c0";

  // Define SCALE constant to match vis-editor
  const SCALE = 3.779528; // 1mm = 3.779528 pixels (96 DPI)

  // Always use white for base material color regardless of block color
  const baseMaterial = { color: "#FFFFFF" };

  // Get gap array from hiVis if available
  const gap = hiVis?.gap || 0;

  // Get global gap value
  const global_gap = hiVis?.design?.global_gap || 0;

  // Function to calculate natural width ratio
  const getNaturalWidthRatio = (character: string) => {
    if (!character) return 1;
    const char = character.toLowerCase();
    if (char === " ") return 0.5; // Space is half width
    if (char === ".") return 0.2; // Dot is very narrow
    if (char === "-") return 0.5; // Dash is half width

    const shape = characterShapes[char as Characters];
    if (!shape) return 1;

    const [, , vbWidth, vbHeight] = shape.viewBox.split(" ").map(Number);
    return vbWidth / vbHeight;
  };

  // Function to handle color conversion
  const getBlockColor = (color: string | undefined) => {
    // Explicitly handle black
    if (color?.toLowerCase() === "#000000") {
      return "#000000";
    } else {
      return baseMaterial.color;
    }
  };

  // Check if bleed is needed by checking all nested blocks
  const checkNeedsBleed = (
    checkBlock: Block,
    rootWidth: number,
    rootHeight: number
  ): boolean => {
    // Check if this block is black and matches root dimensions
    if (
      checkBlock.color === "#000000" &&
      (checkBlock.width === rootWidth || checkBlock.height === rootHeight)
    ) {
      return true;
    }

    // Recursively check child blocks
    if (checkBlock.blocks) {
      for (const child of checkBlock.blocks) {
        if (checkNeedsBleed(child, rootWidth, rootHeight)) {
          return true;
        }
      }
    }

    return false;
  };

  const needsBleed = checkNeedsBleed(block, block.width, block.height);

  // Create SVG string from block data with simplified structure
  const svgContent = `
    <svg 
      width="${width}mm" 
      height="${height}mm" 
      viewBox="0 0 ${width} ${height}" 
      xmlns="http://www.w3.org/2000/svg"
      xmlns:xlink="http://www.w3.org/1999/xlink"
      version="1.1"
    >
      <!-- SVG with${needsBleed ? "" : "out"} bleed -->
      ${
        needsBleed
          ? `<!-- Black bleed background -->
      <rect x="0" y="0" width="${width}" height="${height}" fill="#000000" />`
          : ""
      }
      ${renderBlockToSVG(block, 0, 0, block.width, block.height, 0, hiVis)}
    </svg>
  `;
  return svgContent;

  // Include renderBlockToSVG and renderBrandingToSVG as nested functions
  function renderBlockToSVG(
    block: Block,
    x: number,
    y: number,
    width: number,
    height: number,
    depth: number = 0,
    hiVis: HiVis
  ): string {
    const elements: string[] = [];

    // Calculate the bleed offset (2mm on each side)
    const bleedOffset = 2;

    // Helper function to check if any nested block needs bleed
    const checkNeedsBleed = (
      checkBlock: Block,
      rootWidth: number,
      rootHeight: number
    ): boolean => {
      // Check if this block is black and matches root dimensions
      if (
        checkBlock.color === "#000000" &&
        (checkBlock.width === rootWidth || checkBlock.height === rootHeight)
      ) {
        return true;
      }

      // Recursively check child blocks
      if (checkBlock.blocks) {
        for (const child of checkBlock.blocks) {
          if (checkNeedsBleed(child, rootWidth, rootHeight)) {
            return true;
          }
        }
      }

      return false;
    };

    // Check if bleed is needed
    const needsBleed =
      depth === 0 && checkNeedsBleed(block, block.width, block.height);

    // For the root block, we need to center it in the bleed area
    let designX = x;
    let designY = y;

    if (depth === 0 && needsBleed) {
      // Center the design within the bleed area
      designX = bleedOffset;
      designY = bleedOffset;
    }

    // Calculate the actual scale factor applied to this block for rendering
    // The gaps should be proportional to how the block has been scaled
    const scaleX = width / (block.width * SCALE);
    const scaleY = height / (block.height * SCALE);
    const renderScale = Math.min(scaleX, scaleY); // Use the smaller scale to maintain aspect ratio

    // Check if this should have a transparent background:
    // 1. Character containers (containers that directly contain character blocks)
    // 2. Individual character blocks themselves
    const shouldBeTransparent =
      (block.isContainer &&
        block.blocks &&
        block.blocks.some(
          (childBlock) => childBlock.character !== undefined
        )) ||
      block.character !== undefined;

    if (!shouldBeTransparent) {
      // If corner radius is specified and block is a container, draw rounded rectangle
      if (block.cornerRadius && block.isContainer) {
        const radius = block.cornerRadius * SCALE * renderScale;

        elements.push(
          `<rect x="${designX}" y="${designY}" width="${width}" height="${height}" rx="${radius}" ry="${radius}" fill="${getBlockColor(block.color)}" />`
        );
      } else {
        elements.push(
          `<rect x="${designX}" y="${designY}" width="${width}" height="${height}" fill="${getBlockColor(block.color)}" />`
        );
      }
    }

    if (block.blocks && block.blocks.length > 0) {
      // Special handling for vertical stacked blocks
      if (block.verticalStack && block.blocks) {
        const totalBlocks = block.blocks.length;
        let currentY = designY;

        // Calculate total vertical gaps with proper scaling
        const totalVerticalGap = block.blocks.reduce((sum, b, i) => {
          if (i < totalBlocks - 1) {
            return sum + (b.gap || 0);
          }
          return sum;
        }, 0);

        // Calculate available height for blocks with properly scaled gaps
        const availableHeight = height - totalVerticalGap * SCALE * renderScale;
        const blockHeight = availableHeight / totalBlocks;

        // Handle vertical stacking of characters
        block.blocks.forEach((childBlock) => {
          // For vertical stack, width can be adjusted to maintain proportions
          let childWidth = width;
          const childHeight = blockHeight;

          // If useNaturalWidth is true, we'll center the character horizontally
          if (useNaturalWidth && childBlock.character) {
            const naturalRatio = getNaturalWidthRatio(childBlock.character);
            const characterWidth = childHeight * naturalRatio;
            // Center the character if it's smaller than the container
            if (characterWidth < width) {
              const horizontalPadding = (width - characterWidth) / 2;
              childWidth = characterWidth;
              elements.push(
                ...renderBlockToSVG(
                  childBlock,
                  designX + horizontalPadding,
                  currentY,
                  childWidth,
                  childHeight,
                  depth + 1,
                  hiVis
                ).split("\n")
              );
            } else {
              // If character would be wider than container, use container width
              elements.push(
                ...renderBlockToSVG(
                  childBlock,
                  designX,
                  currentY,
                  width,
                  childHeight,
                  depth + 1,
                  hiVis
                ).split("\n")
              );
            }
          } else {
            // Render at full width if not using natural width
            elements.push(
              ...renderBlockToSVG(
                childBlock,
                designX,
                currentY,
                width,
                childHeight,
                depth + 1,
                hiVis
              ).split("\n")
            );
          }

          // Move down for next character, adding vertical gap if needed with proper scaling
          currentY += childHeight + (childBlock.gap || 0) * SCALE * renderScale;
        });

        return elements.join("\n");
      }

      // Regular horizontal layout
      const isVertical = width < height;
      const availableSpace = isVertical ? height : width;

      // Separate blocks by type for proper distribution
      const regularBlocks = block.blocks.filter(
        (b) => !b.isContainer || (b.isContainer && !b.verticalStack)
      );
      const stackedContainers = block.blocks.filter(
        (b) => b.isContainer && b.verticalStack
      );

      // Count effective blocks for distribution (each stacked container counts as one block)
      const effectiveBlockCount =
        regularBlocks.length + stackedContainers.length;

      // Calculate total gaps using the gap array if available
      const totalGaps = block.blocks.reduce((sum, b, i) => {
        if (i < block.blocks!.length - 1) {
          // Use the gap value directly from the block if available,
          // otherwise fallback to gap array or global gap
          const gapValue =
            b.gap !== undefined
              ? b.gap
              : Array.isArray(gap) && gap[i] !== undefined
                ? gap[i]
                : b.gapLocked
                  ? global_gap
                  : 0;

          return sum + gapValue;
        }
        return sum;
      }, 0);

      const spaceForBlocks = availableSpace - totalGaps * SCALE * renderScale;

      // Calculate total natural ratio if needed
      const totalNaturalRatio =
        useNaturalWidth && !isVertical
          ? block.blocks.reduce((sum, b) => {
              if (b.isContainer) {
                if (b.verticalStack) {
                  // For stacked containers, get the wider character's natural ratio
                  if (b.blocks && b.blocks.length >= 2) {
                    const firstRatio = getNaturalWidthRatio(
                      b.blocks[0].character || ""
                    );
                    const secondRatio = getNaturalWidthRatio(
                      b.blocks[1].character || ""
                    );
                    return sum + Math.max(firstRatio, secondRatio);
                  }
                }
                return sum + 1;
              }
              return sum + getNaturalWidthRatio(b.character || "");
            }, 0)
          : effectiveBlockCount;

      let currentX = designX;
      let currentY = designY;

      // Process all blocks
      for (let index = 0; index < block.blocks.length; index++) {
        const childBlock = block.blocks[index];
        let childX: number;
        let childY: number;
        let childWidth: number;
        let childHeight: number;

        // Handle stacked containers specifically
        if (childBlock.isContainer && childBlock.verticalStack) {
          if (isVertical) {
            // For vertical layouts, stacked containers take full width
            childWidth = width;
            // Calculate height based on position in container
            const blockSize = spaceForBlocks / effectiveBlockCount;
            childHeight = blockSize;
            childX = designX;
            childY = currentY;
            currentY += childHeight;
          } else {
            // For horizontal layouts, calculate width based on distribution
            if (useNaturalWidth) {
              // Get natural ratio for the stacked container
              let stackRatio = 1;
              if (childBlock.blocks && childBlock.blocks.length > 0) {
                // Use max ratio of any characters in the stack
                stackRatio = childBlock.blocks.reduce((max, stackedChild) => {
                  if (stackedChild.character) {
                    const charRatio = getNaturalWidthRatio(
                      stackedChild.character
                    );
                    return Math.max(max, charRatio);
                  }
                  return max;
                }, 0.5);
              }
              childWidth = (spaceForBlocks * stackRatio) / totalNaturalRatio;
            } else {
              // Uniform distribution
              childWidth = spaceForBlocks / effectiveBlockCount;
            }
            childHeight = height;
            childX = currentX;
            childY = designY;
          }

          // Render the stacked container
          elements.push(
            ...renderBlockToSVG(
              childBlock,
              childX,
              childY,
              childWidth,
              childHeight,
              depth + 1,
              hiVis
            ).split("\n")
          );

          // Update currentX position for next block
          if (!isVertical) {
            currentX += childWidth;
          }

          // Add gap after this block if it's not the last one
          if (index < block.blocks.length - 1) {
            // For stacked containers, use the gap stored on the stacked container itself
            const gapValue =
              childBlock.gap !== undefined
                ? childBlock.gap
                : Array.isArray(gap) && index < gap.length
                  ? gap[index]
                  : childBlock.gapLocked
                    ? global_gap
                    : 0;

            if (gapValue > 0) {
              if (isVertical) {
                // Skip gap fill to make gaps transparent
                // elements.push(
                //   `<rect x="${designX}" y="${currentY}" width="${width}" height="${gapValue * SCALE * renderScale}" fill="${getBlockColor(block.color)}" />`
                // );

                currentY += gapValue * SCALE * renderScale;
              } else {
                // Skip gap fill to make gaps transparent
                // elements.push(
                //   `<rect x="${currentX}" y="${designY}" width="${gapValue * SCALE * renderScale}" height="${height}" fill="${getBlockColor(block.color)}" />`
                // );

                currentX += gapValue * SCALE * renderScale;
              }
            }
          }

          continue; // Skip to next block since we've handled this one
        }

        if (childBlock.isContainer) {
          // Use container's actual dimensions
          childWidth = (childBlock.width / block.width) * width;
          childHeight = (childBlock.height / block.height) * height;

          // Center the container
          const blocks = block.blocks;
          const allocatedWidth = isVertical ? width : width / blocks.length;
          const allocatedHeight = isVertical ? height / blocks.length : height;

          // Calculate total gaps before this block
          const gapsBefore = blocks.slice(0, index).reduce((sum, b, i) => {
            const gapValue =
              Array.isArray(gap) && gap[i] !== undefined
                ? gap[i]
                : b.gapLocked
                  ? global_gap
                  : (b.gap ?? 0);
            return sum + gapValue;
          }, 0);

          const allocatedX = isVertical
            ? designX
            : designX +
              index * allocatedWidth +
              gapsBefore * SCALE * renderScale;
          const allocatedY = isVertical
            ? designY +
              index * allocatedHeight +
              gapsBefore * SCALE * renderScale
            : designY;

          childX = allocatedX + (allocatedWidth - childWidth) / 2;
          childY = allocatedY + (allocatedHeight - childHeight) / 2;
        } else {
          // For character blocks

          // Special handling for character blocks when they have predefined widths
          // This prevents extra space at the end when container is larger than content
          if (childBlock.character && childBlock.width && !isVertical) {
            // Use the predefined width from the block data
            childWidth = (childBlock.width / block.width) * width;
            childHeight = height;
            childX = currentX;
            childY = designY;

            // Update currentX based on actual character width
            currentX += childWidth;
          } else if (useNaturalWidth && !isVertical) {
            // Calculate width based on natural ratio
            const naturalRatio = getNaturalWidthRatio(
              childBlock.character || ""
            );

            // Calculate proportional width based on natural ratio
            const blockWidth =
              (spaceForBlocks * naturalRatio) / totalNaturalRatio;

            childX = currentX;
            childY = designY;
            childWidth = blockWidth;
            childHeight = height;

            // Update currentX for next character
            currentX += blockWidth;
          } else {
            // Use uniform distribution
            const nonContainerBlocks = block.blocks.filter(
              (b) => !b.isContainer || (b.isContainer && !b.verticalStack)
            ).length;
            const blockSize = spaceForBlocks / nonContainerBlocks;

            if (isVertical) {
              childX = designX;
              childY = currentY;

              // For vertical layouts, if using natural width, calculate width based on character ratio
              if (useNaturalWidth && childBlock.character) {
                childHeight = blockSize;
                const naturalRatio = getNaturalWidthRatio(childBlock.character);
                // Calculate width based on natural ratio to maintain proportions
                const naturalWidth = childHeight * naturalRatio;

                // If natural width is less than container width, center it horizontally
                if (naturalWidth < width) {
                  childWidth = naturalWidth;
                  // Center the character horizontally
                  childX = designX + (width - childWidth) / 2;
                } else {
                  // If natural width would exceed container width, cap it
                  childWidth = width;
                }
              } else {
                // Default behavior for non-natural width mode
                childWidth = width;
                childHeight = blockSize;
              }

              currentY += blockSize;
            } else {
              childX = currentX;
              childY = designY;
              childWidth = blockSize;
              childHeight = height;
              currentX += blockSize;
            }
          }
        }

        elements.push(
          ...renderBlockToSVG(
            childBlock,
            childX,
            childY,
            childWidth,
            childHeight,
            depth + 1,
            hiVis
          ).split("\n")
        );

        // Add gap after this character if it's not the last one
        if (index < block.blocks.length - 1) {
          // Get the gap value for this block
          const gapValue =
            childBlock.gap !== undefined
              ? childBlock.gap
              : Array.isArray(gap) && index < gap.length
                ? gap[index]
                : childBlock.gapLocked
                  ? global_gap
                  : 0;

          if (gapValue > 0) {
            if (isVertical) {
              // Skip gap fill to make gaps transparent
              // elements.push(
              //   `<rect x="${designX}" y="${currentY}" width="${width}" height="${gapValue * SCALE * renderScale}" fill="${getBlockColor(block.color)}" />`
              // );

              currentY += gapValue * SCALE * renderScale;
            } else {
              // Skip gap fill to make gaps transparent
              // elements.push(
              //   `<rect x="${currentX}" y="${designY}" width="${gapValue * SCALE * renderScale}" height="${height}" fill="${getBlockColor(block.color)}" />`
              // );

              currentX += gapValue * SCALE * renderScale;
            }
          }
        }
      }
    } else if (block.character) {
      // Set text color based on background
      const textColor =
        block.color === "#000000"
          ? baseMaterial.color // White text on black background (use base material color)
          : block.color === baseMaterial.color
            ? "#000000" // Black text on base material color
            : "#000000"; // Default black text

      // Special handling for dot and dash characters
      if (
        block.character === "." ||
        block.character === "-" ||
        block.character === " "
      ) {
        if (block.character === ".") {
          const [, , vbWidth, vbHeight] = characterShapes.dot.viewBox
            .split(" ")
            .map(Number);
          const dotHeight = height * 0.8;
          const dotScale = dotHeight / vbHeight;
          const dotWidth = vbWidth * dotScale;

          // Create path for the dot without wrapping in a group
          elements.push(
            `<path d="${characterShapes.dot.path}" fill="${textColor}" transform="translate(${
              designX + (width - dotWidth) / 2
            },${designY + height}) scale(${dotScale},${-dotScale})" />`
          );
        } else if (block.character === "-") {
          const [, , vbWidth, vbHeight] = characterShapes.dash.viewBox
            .split(" ")
            .map(Number);
          const dashWidth = width * 0.75;
          const dashHeight = height;
          const scaleX = dashWidth / vbWidth;
          const scaleY = dashHeight / vbHeight;

          // Create path for the dash without wrapping in a group
          elements.push(
            `<path d="${characterShapes.dash.path}" fill="${textColor}" transform="translate(${
              designX + (width - dashWidth) / 2
            },${
              designY + height / 2 - dashHeight / 12
            }) scale(${scaleX},${scaleY})" />`
          );
        } else if (block.character === " ") {
          elements.push(
            `<line x1="${designX}" y1="${designY + height - 2}" x2="${designX + width}" y2="${designY + height - 2}" stroke="${textColor}" stroke-width="4" />`
          );
        }
      } else {
        const blockKey = block.character.toLowerCase() as Characters;
        const shape = characterShapes[blockKey];

        if (shape) {
          const [, , vbWidth, vbHeight] = shape.viewBox.split(" ").map(Number);
          const scaleX = width / vbWidth;
          const scaleY = height / vbHeight;

          // Create path for the character without wrapping in a group
          elements.push(
            `<path d="${shape.path}" fill="${textColor}" transform="translate(${designX},${designY}) scale(${scaleX},${scaleY})" />`
          );
        }
      }
    }

    // Add cut border with Thru-Cut color at the end (depth === 0 ensures it's only added once)
    if (depth === 0) {
      // Draw the cut line exactly at the design dimensions
      elements.push(
        `<rect x="${designX}" y="${designY}" width="${width}" height="${height}" fill="none" stroke="${THRU_CUT_COLOR}" stroke-width="0.25" />`
      );
    }

    return elements.join("\n");
  }
};

const DownloadCMYKPDF = ({
  hiVis,
  orderNumber,
  partNumber,
  decalNumber,
}: DownloadCMYKPDFProps) => {
  const [isLoading, setIsLoading] = useState(false);

  // Debug log when hiVis changes
  useEffect(() => {
    if (hiVis) {
      // Check if the design blocks contain characters
      if (hiVis.design?.blocks?.length > 0) {
        const firstBlock = hiVis.design.blocks[0];
        // Find character blocks (if any)
        const findCharBlocks = (
          block: Block,
          depth: number = 0,
          path: string = ""
        ): void => {
          if (block.blocks && block.blocks.length > 0) {
            block.blocks.forEach((childBlock, index) => {
              findCharBlocks(childBlock, depth + 1, `${path}>${index}`);
            });
          }
        };

        findCharBlocks(firstBlock, 0, "root");
      }
    }
  }, [hiVis, decalNumber]);

  const blocks = hiVis?.design?.blocks || [];
  const useNaturalWidth = hiVis?.naturalWidth || false;

  // Function to handle download
  const handleDownload = async () => {
    if (!blocks || blocks.length === 0) return;
    if (!hiVis) return;

    try {
      setIsLoading(true);
      const rootBlock = blocks[0];
      const needsBleed =
        rootBlock.blocks?.[0]?.color === "#000000" &&
        (rootBlock.blocks[0].width === rootBlock.width ||
          rootBlock.blocks[0].height === rootBlock.height);

      // Use exact dimensions from the block
      const designWidthMM = rootBlock.width;
      const designHeightMM = rootBlock.height;

      // Add bleed only if needed (exactly 2mm on each side = 4mm total)
      const bleedWidthMM = needsBleed ? designWidthMM + 4 : designWidthMM;
      const bleedHeightMM = needsBleed ? designHeightMM + 4 : designHeightMM;

      // Convert mm to points (1 mm = 2.83465 points)
      const MM_TO_PT = 2.83465;
      const widthPT = bleedWidthMM * MM_TO_PT;
      const heightPT = bleedHeightMM * MM_TO_PT;

      // Generate base filename first
      const baseFilename =
        orderNumber && partNumber && decalNumber
          ? `${orderNumber}_${partNumber}_${decalNumber}`
          : "design-press-ready";

      // Generate SVG content with the correct dimensions
      const svgContent = generateSVG(
        rootBlock,
        bleedWidthMM,
        bleedHeightMM,
        hiVis,
        {
          useNaturalWidth,
        }
      );
      // Only generate and download a single PDF
      const filename = `${baseFilename}.pdf`;

      // Generate PDF on the server
      const base64PDF = await generateSVGPDF(svgContent, widthPT, heightPT);

      // Convert base64 back to Blob
      const binaryString = atob(base64PDF);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      const pressReadyBlob = new Blob([bytes], { type: "application/pdf" });

      // Create download link
      const url = URL.createObjectURL(pressReadyBlob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;

      // Trigger download
      document.body.appendChild(link);
      link.click();

      // Clean up
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error generating PDF:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Button
      onClick={handleDownload}
      className='flex items-center justify-center download-pdf-button'
      variant='default'
      disabled={isLoading}
      title={isLoading ? "Generating..." : "Download Press-Ready PDF"}
    >
      <Download className='h-4 w-4' />
      {isLoading && "Generating..."}
    </Button>
  );
};

export default DownloadCMYKPDF;
